import { Link, useNavigate, useLocation } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import locContext from "../context/locContext";
import "../home/home.css";

const Loader = () => (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(255,255,255,0.8)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
    }}
  >
    <div
      className="spinner-border text-primary"
      style={{
        width: "4rem",
        height: "4rem",
        color: "#139bb2",
        borderWidth: "6px",
      }}
      role="status"
    >
      <span className="visually-hidden">Loading...</span>
    </div>
  </div>
);

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  {
    label: "Our Services",
    dropdown: [
      { label: "Mutual Fund", to: "/mutual-fund" },
      { label: "Loan", to: "/loan" },
    ],
  },
  { label: "About Us", to: "/about" },
  { label: "Careers", to: "/career" },
  { label: "Blogs", to: "/blog" },
  {
    label: "Calculator",
    dropdown: [
      { label: "Sip Calculator", to: "/SipCalculator" },
      { label: "Lumpsum Calculator", to: "/LumpsumCalculator" },
      { label: "Retirement Calculator", to: "/RetirementCalculator" },
      { label: "EMI Calculator", to: "/emi-calculator" },
      { label: "Loan Eligibility Estimator", to: "/loan-eligibility-estimator" },
      { label: "Tenure Optimizer", to: "/tenure-optimizer" },
    ],
  },
  { label: "Contact Us", to: "/contact" },
];

export default function Navbar() {
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showCalculatorDropdown, setShowCalculatorDropdown] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [calculatorOpen, setCalculatorOpen] = useState(false);

  const location = useLocation();
  const { userData } = useContext(locContext);
  const userRoll = userData ? userData.role_name : null;
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  const isAdvisor =
    userRoll &&
    (userRoll === "Advisor" ||
      userRoll === "Agent" ||
      userRoll.toLowerCase() === "advisor" ||
      userRoll.toLowerCase() === "agent");
  const isClient = userRoll === "User" || userRoll === "Client";

  useEffect(() => {
    setTimeout(() => setLoading(false), 600);
  }, []);

  useEffect(() => {
    setIsNavOpen(false);
    setShowServicesDropdown(false);
    setShowCalculatorDropdown(false);
    setServicesOpen(false);
    setCalculatorOpen(false);
  }, [location]);

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      try {
        window.history.scrollRestoration = "manual";
      } catch (e) {}
    }
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isNavOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isNavOpen]);

  useEffect(() => {
    if (!isNavOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeAll();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isNavOpen]);

  const isActive = (to) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to);
  };

  const handleLogout = async () => {
    Swal.fire({
      title: "Logout Confirmation",
      html: `
        <div style="text-align:center;">
          <div style="width:70px;height:70px;margin:0 auto 15px auto;border-radius:50%;background:#139bb23d;display:flex;align-items:center;justify-content:center;">
            <i class="fa fa-sign-out-alt" style="font-size:32px;color:#139bb2;"></i>
          </div>
          <p style="font-size:16px;color:#555;margin:0;">
            Are you sure you want to logout from your account?
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Yes, Logout",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        confirmButton: "custom-confirm-btn",
        cancelButton: "custom-cancel-btn",
      },
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios
          .post(`${process.env.REACT_APP_BACKEND_BASE_URL}/user/logout`)
          .then((res) => {
            if (res.status === 200) {
              localStorage.removeItem(process.env.REACT_APP_LOGIN_DATA_STORE);
              Swal.fire({
                html: `
                  <div style="text-align:center;padding:20px 10px;">
                    <div style="width:80px;height:80px;margin:0 auto 20px auto;border-radius:50%;background:#139bb23d;display:flex;align-items:center;justify-content:center;">
                      <i class="fa fa-check" style="font-size:36px;color:#139bb2;"></i>
                    </div>
                    <h2 style="margin:0;font-size:22px;color:#333;">Logged out successfully</h2>
                  </div>
                `,
                showConfirmButton: false,
                timer: 1500,
                customClass: { popup: "custom-swal-popup" },
              });
              navigate("/");
            }
          })
          .catch(() => Swal.fire("Error", "Something went wrong", "error"))
          .finally(() => setLoading(false));
      }
    });
  };

  const closeAll = () => {
    setIsNavOpen(false);
    setServicesOpen(false);
    setCalculatorOpen(false);
    setShowServicesDropdown(false);
    setShowCalculatorDropdown(false);
  };

  const renderAuthButtons = () => {
    if (isClient || isAdvisor) {
      return (
        <>
          <Link
            to={isClient ? "/overview" : "/agent/dashboard"}
            className="gps-btn-outline"
            onClick={closeAll}
          >
            Dashboard
          </Link>
          <button type="button" className="gps-btn-primary" onClick={handleLogout}>
            Logout
          </button>
        </>
      );
    }

    return (
      <>
        <a
          href="https://gpsfinvest.investwell.app/app/#/login"
          className="gps-btn-outline"
        >
          Login
        </a>
        <Link to="/registration" className="gps-btn-primary" onClick={closeAll}>
          Get Started
        </Link>
      </>
    );
  };

  if (loading) return <Loader />;

  return (
    <>
      <style>{`
        .custom-swal-popup { border-radius: 16px !important; padding: 25px !important; }
        .custom-confirm-btn {
          background: #139bb2 !important; color: #fff !important;
          padding: 10px 22px !important; border-radius: 8px !important;
          border: none !important; margin-right: 10px; font-size: 14px;
        }
        .custom-cancel-btn {
          background: #ef4444 !important; color: #fff !important;
          padding: 10px 22px !important; border-radius: 8px !important;
          border: none !important; font-size: 14px;
        }
      `}</style>

      <header className={`gps-navbar ${isHome ? "home-nav" : ""}`}>
        <div className="gps-navbar-inner">
          <Link
            to="/"
            className="gps-navbar-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "instant" })}
          >
            <img src="/images/gpslogo.png" alt="GPS Global Portfolio Solutions" />
          </Link>

          <ul className="gps-nav-links gps-nav-links-desktop">
            {NAV_ITEMS.map((item) =>
              item.dropdown ? (
                <li
                  key={item.label}
                  className={`gps-nav-dropdown ${
                    item.label === "Our Services"
                      ? showServicesDropdown
                        ? "open"
                        : ""
                      : showCalculatorDropdown
                      ? "open"
                      : ""
                  }`}
                  onMouseEnter={() =>
                    item.label === "Our Services"
                      ? setShowServicesDropdown(true)
                      : setShowCalculatorDropdown(true)
                  }
                  onMouseLeave={() =>
                    item.label === "Our Services"
                      ? setShowServicesDropdown(false)
                      : setShowCalculatorDropdown(false)
                  }
                >
                  <span className="gps-nav-link" style={{ cursor: "pointer" }}>
                    {item.label}{" "}
                    <i
                      className="fa-solid fa-chevron-down"
                      style={{ fontSize: "10px", marginLeft: "2px" }}
                    />
                  </span>
                  <div className="dropdown-menu">
                    {item.dropdown.map((sub) => (
                      <Link
                        key={sub.to}
                        className="dropdown-item"
                        to={sub.to}
                        onClick={closeAll}
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </li>
              ) : (
                <li key={item.to}>
                  <Link
                    className={`gps-nav-link ${isActive(item.to) ? "active" : ""}`}
                    to={item.to}
                    onClick={closeAll}
                  >
                    {item.label}
                  </Link>
                </li>
              )
            )}
          </ul>

          <div className="gps-nav-actions gps-nav-actions-desktop">
            {renderAuthButtons()}
          </div>

          <button
            className="gps-navbar-toggler"
            type="button"
            aria-expanded={isNavOpen}
            aria-label={isNavOpen ? "Close navigation" : "Open navigation"}
            onClick={() => setIsNavOpen((s) => !s)}
          >
            <i className={`fa-solid ${isNavOpen ? "fa-xmark" : "fa-bars"}`} />
          </button>
        </div>
      </header>

      {!isHome && <div style={{ height: 76 }} aria-hidden="true" />}

      <div
        className={`gps-drawer-backdrop${isNavOpen ? " is-open" : ""}`}
        onClick={closeAll}
        aria-hidden={!isNavOpen}
      />

      <aside
        className={`gps-drawer${isNavOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        aria-hidden={!isNavOpen}
      >
        <div className="gps-drawer-head">
          <Link to="/" className="gps-drawer-logo" onClick={closeAll}>
            <img src="/images/gpslogo.png" alt="GPS Global Portfolio Solutions" />
          </Link>
          <button
            type="button"
            className="gps-drawer-close"
            aria-label="Close menu"
            onClick={closeAll}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <nav className="gps-drawer-body">
          {NAV_ITEMS.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="gps-drawer-item">
                <button
                  type="button"
                  className={`gps-drawer-expand${
                    (item.label === "Our Services" ? servicesOpen : calculatorOpen)
                      ? " is-open"
                      : ""
                  }`}
                  onClick={() =>
                    item.label === "Our Services"
                      ? setServicesOpen((s) => !s)
                      : setCalculatorOpen((s) => !s)
                  }
                >
                  <span>{item.label}</span>
                  <i className="fa-solid fa-chevron-right" aria-hidden="true" />
                </button>
                <div
                  className={`gps-drawer-sub${
                    (item.label === "Our Services" ? servicesOpen : calculatorOpen)
                      ? " is-open"
                      : ""
                  }`}
                >
                  {item.dropdown.map((sub) => (
                    <Link
                      key={sub.to}
                      className={`gps-drawer-link gps-drawer-sublink${
                        isActive(sub.to) ? " is-active" : ""
                      }`}
                      to={sub.to}
                      onClick={closeAll}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <div key={item.to} className="gps-drawer-item">
                <Link
                  className={`gps-drawer-link${isActive(item.to) ? " is-active" : ""}`}
                  to={item.to}
                  onClick={closeAll}
                >
                  {item.label}
                </Link>
              </div>
            )
          )}
        </nav>

        <div className="gps-drawer-actions">{renderAuthButtons()}</div>
      </aside>
    </>
  );
}
